"use client";

import { c as _c } from "react/compiler-runtime";
import { useChainSearch } from "@/hooks/useChainSearch";
import { addRecentChain, type RecentChain } from "@/lib/utils/recentSelections";
import { fetchChainById, formatChainOutput, normalizeChainValue, toSelectableChain, type Chain, type ChainSelectorValueFormat } from "@/lib/utils/chainSelection";
import * as React from "react";
import ChainSelectorView from "./ChainSelectorView";
interface ChainSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: number | string | undefined;
  onChange: (value: number | string) => void;
  /** When false, hides the testnet toggle and keeps the selector on mainnets only. */
  showTestnetToggle?: boolean;
  /** When true, only show x402-supported chains from tokens.b3.fun */
  x402?: boolean;
  /** Output format: 'chainId' (default) outputs numeric chain ID, 'networkId' outputs string like 'eth', 'bsc' */
  valueFormat?: ChainSelectorValueFormat;
  /** When true, Popover uses modal mode (required inside dialogs to receive pointer events) */
  popoverModal?: boolean;
  /**
   * Restricts the selector to only show these chain IDs.
   * Sourced from the action schema's `enum` field (e.g. Railgun only supports [1, 137, 42161, 56]).
   * When set: search results, recent chains, and testnet toggle are all filtered/hidden.
   * When absent: all chains are shown (default behavior).
   */
  allowedChainIds?: number[];
  /** Chain IDs to exclude from the selector (e.g., exclude Solana chains for EVM-only actions). */
  excludeChainIds?: number[];
}
function ChainSelector(t0) {
  const $ = _c(34);
  const {
    label,
    description,
    headerRight,
    value,
    onChange,
    showTestnetToggle: t1,
    x402: t2,
    valueFormat: t3,
    popoverModal: t4,
    allowedChainIds,
    excludeChainIds
  } = t0;
  const showTestnetToggle = t1 === undefined ? true : t1;
  const x402 = t2 === undefined ? false : t2;
  const valueFormat = t3 === undefined ? "chainId" : t3;
  const popoverModal = t4 === undefined ? false : t4;
  const [open, setOpen] = React.useState(false);
  const [selectedChain, setSelectedChain] = React.useState(null);
  let t5;
  let t6;
  if ($[0] !== value) {
    t6 = normalizeChainValue(value);
    $[0] = value;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  t5 = t6;
  const numericValue = t5;
  let t7;
  if ($[2] !== allowedChainIds || $[3] !== excludeChainIds || $[4] !== open || $[5] !== x402) {
    t7 = {
      allowedChainIds,
      excludeChainIds,
      open,
      x402
    };
    $[2] = allowedChainIds;
    $[3] = excludeChainIds;
    $[4] = open;
    $[5] = x402;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const {
    handleSearchChange,
    isLoading,
    orderedChains,
    recentChains,
    refreshRecentChains,
    searchQuery,
    testnetFilter,
    setTestnetFilter
  } = useChainSearch(t7);
  let t8;
  let t9;
  if ($[7] !== numericValue || $[8] !== selectedChain) {
    t8 = () => {
      const loadInitialChain = async () => {
        if (numericValue && !Number.isNaN(numericValue)) {
          if (!selectedChain || selectedChain.chainId !== numericValue) {
            ;
            try {
              const chain = await fetchChainById(numericValue);
              if (chain) {
                setSelectedChain(chain);
              }
            } catch (t10) {
              const error = t10;
              console.error("Failed to load initial chain:", error);
            }
          }
        }
      };
      loadInitialChain();
    };
    t9 = [numericValue, selectedChain];
    $[7] = numericValue;
    $[8] = selectedChain;
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  React.useEffect(t8, t9);
  let t10;
  if ($[11] !== onChange || $[12] !== refreshRecentChains || $[13] !== valueFormat) {
    t10 = chain_0 => {
      const chainData = toSelectableChain(chain_0);
      setSelectedChain(chainData);
      onChange(formatChainOutput([chain_0.chainId], valueFormat)[0] as number | string);
      setOpen(false);
      addRecentChain({
        chainId: chain_0.chainId,
        name: chain_0.name,
        iconUrl: chain_0.iconUrl || ""
      });
      refreshRecentChains();
    };
    $[11] = onChange;
    $[12] = refreshRecentChains;
    $[13] = valueFormat;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const handleSelect = t10;
  const effectiveShowTestnetToggle = showTestnetToggle && !x402 && !allowedChainIds?.length;
  let t11;
  if ($[15] !== setTestnetFilter) {
    t11 = next => setTestnetFilter(next);
    $[15] = setTestnetFilter;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== description || $[18] !== effectiveShowTestnetToggle || $[19] !== handleSearchChange || $[20] !== handleSelect || $[21] !== headerRight || $[22] !== isLoading || $[23] !== label || $[24] !== numericValue || $[25] !== open || $[26] !== orderedChains || $[27] !== popoverModal || $[28] !== recentChains || $[29] !== searchQuery || $[30] !== selectedChain || $[31] !== t11 || $[32] !== testnetFilter) {
    t12 = <ChainSelectorView label={label} description={description} headerRight={headerRight} selectedChain={selectedChain} orderedChains={orderedChains} recentChains={recentChains} isLoading={isLoading} searchQuery={searchQuery} onSearchChange={handleSearchChange} open={open} onOpenChange={setOpen} numericValue={numericValue} onSelect={handleSelect} showTestnetToggle={effectiveShowTestnetToggle} testnetFilter={testnetFilter} onTestnetFilterChange={t11} popoverModal={popoverModal} />;
    $[17] = description;
    $[18] = effectiveShowTestnetToggle;
    $[19] = handleSearchChange;
    $[20] = handleSelect;
    $[21] = headerRight;
    $[22] = isLoading;
    $[23] = label;
    $[24] = numericValue;
    $[25] = open;
    $[26] = orderedChains;
    $[27] = popoverModal;
    $[28] = recentChains;
    $[29] = searchQuery;
    $[30] = selectedChain;
    $[31] = t11;
    $[32] = testnetFilter;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
export default React.memo(ChainSelector);